import ZTable, { IFilterItem, IColumnType, ITableRef } from '@/components/ZTable';
import { formatDisplayFullDate } from '@/config';
import { TicketStatusType } from '@/constants';
import i18n from '@/helpers/i18n';
import MainContent from '@/layouts/MainContent';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { getFullName } from '@/helpers/string';
import StationInput from '@/components/FormItems/StationInput';
import { Tooltip, Button, Tag } from 'antd';
import { UsergroupAddOutlined, SafetyOutlined, InfoCircleOutlined, PrinterOutlined, HistoryOutlined } from '@ant-design/icons';
import UpdatePassengerModal from './components/updatePassengerModal';
import UpdatePassengerIdentityModal from './components/updatePassengerIdentityModal';
import BookingChangeModal from '../Bookings/modals/bookingChange.modal';
import { sumBy } from 'lodash';
import { TicketPagingDto } from '@/__generated';
import useHasPermissions from '@/hooks/useHasPermissions';
import { APP_PERMISSIONS } from '@/services/roleService';
import BooleanTag from '@/components/app/BooleanTag';
import TicketBaggageInfo from './components/TicketBaggageInfo';
import TicketReprintsModal from './components/TicketReprintsModal';
import TicketModificationsModal from './components/TicketModificationsModal';

const PAGE_TITLE = 'manageTicket_Title';

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'BookingRefNo', label: 'ticketBooking_BookingRefNo' },
  { type: 'TEXT', name: 'TicketRefNo', label: 'ticketBooking_TicketRefNo' },
  {
    type: 'SELECT',
    name: 'sourceId',
    label: 'ticketBooking_FromStation',
    customElm: <StationInput placeholder={i18n.t('ticketBooking_FromStation') as any} />,
  },
  {
    type: 'SELECT',
    name: 'DestinationId',
    label: 'ticketBooking_ToStation',
    customElm: <StationInput placeholder={i18n.t('ticketBooking_ToStation') as any} />,
  },
  {
    type: 'LIST',
    name: 'status',
    label: 'base.Status',
    listData: TicketStatusType.__getSelectOptions(),
  },
];

const columns: IColumnType<TicketPagingDto>[] = [
  {
    title: 'ticketBooking_BookingRefNo',
    dataIndex: 'bookingRefNo',
    ellipsis: true,
    width: 150,
  },
  {
    title: 'ticketBooking_TicketRefNo',
    dataIndex: 'ticketRefNo',
    sorter: true,
    ellipsis: true,
    width: 150,
  },
  {
    title: 'ticketBooking_FromStation',
    dataIndex: 'sourceStation',
    ellipsis: true,
    width: 150,
  },
  {
    title: 'ticketBooking_ToStation',
    dataIndex: 'destinationStation',
    ellipsis: true,
    width: 150,
  },
  {
    title: 'tripsPage_TripName',
    dataIndex: 'tripName',
    ellipsis: true,
    width: 140,
  },
  {
    title: 'base.Status',
    dataIndex: 'ticketStatus',
    sorter: true,
    ellipsis: true,
    render: v => <Tag color={TicketStatusType.__getBgColor(v)}>{TicketStatusType.__getValue(v)}</Tag>,
    width: 120,
  },
  {
    title: 'ticketReprints_Column',
    dataIndex: 'reprintCount',
    width: 110,
    render: v => (v ? <Tag color={v >= 3 ? 'orange' : 'blue'}>{v}</Tag> : '0'),
  },
  {
    title: 'ticketBooking_Passenger',
    dataIndex: 'passengerFirstName',
    ellipsis: true,
    width: 150,
    render: (_, record) => getFullName(record.passengerFirstName, record.passengerLastName),
  },
  {
    title: 'ticketBooking_SeatNo',
    dataIndex: 'seatName',
    ellipsis: true,
    width: 100,
    render: (_, row) => [row.seatName, row.ticketClassName?.at(0)].join(' - '),
  },
  {
    title: 'bookingPage_HasBaggage',
    dataIndex: 'baggageList',
    width: 120,
    ellipsis: true,
    align: 'center',
    render: (v, r) => <TicketBaggageInfo baggages={v || []} />,
  },
  {
    title: 'bookingPage_DepartureDate',
    dataIndex: 'departureDateTime',
    width: 170,
    ellipsis: true,
    render: v => (v ? formatDisplayFullDate(v) : ''),
    sorter: true,
  },

  {
    title: 'ticketBooking_BookingDate',
    dataIndex: 'createdOn',
    width: 170,
    ellipsis: true,
    render: v => (v ? formatDisplayFullDate(v) : ''),
    sorter: true,
  },
];

const width = sumBy(columns, x => x.width || 0);
export default function ManageTicketPage() {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [updateTicketPassengerId, setUpdateTicketPassengerId] = React.useState<string>();
  const [updateTicketPassengerIdentityId, setUpdateTicketPassengerIdentityId] = React.useState<string>();
  const [tripChangeId, setTripChangeId] = React.useState<string>();
  const [reprintsOf, setReprintsOf] = React.useState<TicketPagingDto>();
  const [modificationsOf, setModificationsOf] = React.useState<TicketPagingDto>();
  const canUpdatePassengers = useHasPermissions([APP_PERMISSIONS.ManageTickets, APP_PERMISSIONS.ManagePassenger]);
  return (
    <MainContent title={t(PAGE_TITLE)}>
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable<TicketPagingDto>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/bookings/booking-details/get-ticket-paging"
          bordered
          scroll={{ x: width }}
          order_by="createdOn"
          order_by_asc={false}
          allowEdit={false}
          allowDelete={false}
          filters={filters}
          searchBtnAlignLeft
          actionColumnWidth={canUpdatePassengers ? 160 : 110}
          rowClassName={row => (row.onlineBookingId ? 'highlight-row' : '')}
          extraActions={[
            {
              render: record => {
                return canUpdatePassengers ? (
                  <Tooltip title={t('manageTicket_UpdatePassenger')}>
                    <Button
                      size="small"
                      onClick={() => {
                        setUpdateTicketPassengerId(record.ticketId);
                      }}
                      type="primary"
                      icon={<UsergroupAddOutlined />}
                    />
                  </Tooltip>
                ) : null;
              },
            },
            {
              render: record => {
                return canUpdatePassengers ? (
                  <Tooltip title={t('manageTicket_UpdatePassengerIdentity')}>
                    <Button
                      size="small"
                      onClick={() => {
                        setUpdateTicketPassengerIdentityId(record.ticketId);
                      }}
                      type="primary"
                      icon={<SafetyOutlined />}
                    />
                  </Tooltip>
                ) : null;
              },
            },
            {
              render: record => {
                return (
                  <Tooltip title={t('ticketReprints_Title')}>
                    <Button size="small" onClick={() => setReprintsOf(record)} type="primary" icon={<PrinterOutlined />} />
                  </Tooltip>
                );
              },
            },
            {
              render: record => {
                return (
                  <Tooltip title={t('ticketModifications_Title')}>
                    <Button size="small" onClick={() => setModificationsOf(record)} type="primary" icon={<HistoryOutlined />} />
                  </Tooltip>
                );
              },
            },
            {
              render: record => {
                return (
                  <Tooltip title={t('bookingPage_BookingHistory')}>
                    <Button
                      size="small"
                      onClick={() => {
                        setTripChangeId(record.tripId);
                      }}
                      type="primary"
                      icon={<InfoCircleOutlined />}
                    />
                  </Tooltip>
                );
              },
            },
          ]}
        />
      </div>

      {updateTicketPassengerId ? (
        <UpdatePassengerModal
          ticketId={updateTicketPassengerId}
          onClose={(reload: boolean) => {
            setUpdateTicketPassengerId(undefined);
            if (reload) {
              zTb.current?.reload();
            }
          }}
        />
      ) : null}
      {updateTicketPassengerIdentityId ? (
        <UpdatePassengerIdentityModal
          ticketId={updateTicketPassengerIdentityId}
          onClose={(reload: boolean) => {
            setUpdateTicketPassengerIdentityId(undefined);
            if (reload) {
              zTb.current?.reload();
            }
          }}
        />
      ) : null}
      {tripChangeId ? <BookingChangeModal tripId={tripChangeId} onClose={() => setTripChangeId(undefined)} /> : null}
      {reprintsOf?.ticketId ? (
        <TicketReprintsModal ticketId={reprintsOf.ticketId} ticketRefNo={reprintsOf.ticketRefNo} onClose={() => setReprintsOf(undefined)} />
      ) : null}
      {modificationsOf?.ticketId ? (
        <TicketModificationsModal
          ticketId={modificationsOf.ticketId}
          ticketRefNo={modificationsOf.ticketRefNo}
          onClose={() => setModificationsOf(undefined)}
        />
      ) : null}
    </MainContent>
  );
}
